<template>
  <div class="mermaid-wrap">
    <div v-if="error" class="mermaid-fallback">
      <p class="mermaid-note">Diagram preview unavailable — showing source:</p>
      <pre>{{ code }}</pre>
    </div>
    <div v-else class="mermaid-host" v-html="svg" />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'

/*
 * Renders a single Mermaid diagram. Mermaid is imported lazily so it only loads
 * on slides that actually contain a diagram, and so unit tests that import the
 * registry never pull the heavy dependency.
 */
let mermaidPromise = null
async function getMermaid() {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then((mod) => {
      const mermaid = mod.default
      mermaid.initialize({
        startOnLoad: false,
        securityLevel: 'loose',
        theme: 'base',
        fontFamily: 'inherit',
        flowchart: { htmlLabels: true, useMaxWidth: true },
        sequence: { useMaxWidth: true },
      })
      return mermaid
    })
  }
  return mermaidPromise
}

const props = defineProps({
  code: { type: String, required: true },
})

const svg = ref('')
const error = ref(false)
let seq = 0

async function render() {
  error.value = false
  const code = (props.code || '').trim()
  if (!code) {
    svg.value = ''
    return
  }
  try {
    const mermaid = await getMermaid()
    seq += 1
    const id = `mmd-${Date.now()}-${seq}`
    const { svg: out } = await mermaid.render(id, code)
    svg.value = out
  } catch {
    // Leave the raw source visible rather than breaking the slide.
    error.value = true
  }
}

onMounted(render)
watch(() => props.code, render)
</script>

<style scoped>
.mermaid-wrap {
  margin: 1.1rem 0;
}

.mermaid-host {
  display: flex;
  justify-content: center;
}

.mermaid-host :deep(svg) {
  max-width: 100%;
  height: auto;
}

.mermaid-fallback {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--paper-warm);
  padding: 0.85rem 1rem;
}

.mermaid-note {
  margin: 0 0 0.5rem;
  font-size: 0.8rem;
  color: var(--muted);
}

.mermaid-fallback pre {
  margin: 0;
  overflow-x: auto;
  font-size: 0.8rem;
  white-space: pre-wrap;
}
</style>
